/**
 * Collection Page Quantity Selector - Fixed h-9 (36px) height
 * Handles the "Dropdown to Input" transition for 10+ quantities.
 */
class QuantitySelectorCollection {
    /**
     * @param {HTMLElement} container
     */
    constructor(container) {
        /** @type {HTMLElement} */
        this.container = container;
        let settingsJson = '';
        const settingsEl = container.querySelector('[data-settings-collection]');
        if (settingsEl) {
            settingsJson = settingsEl.textContent || '{}';
        } else {
            const attr = container.getAttribute('data-settings-collection-json');
            if (attr) {
                // Decode HTML entities when attribute was set via innerHTML (e.g. &quot; -> ")
                settingsJson = attr.replace(/&quot;/g, '"');
            }
        }
        if (!settingsJson) return;
        try {
            this.settings = JSON.parse(settingsJson);
        } catch (e) {
            return;
        }
        this.init();
    }

    init() {
        /** @type {HTMLSelectElement | null} */
        this.select = this.container.querySelector('[data-quantity-select-collection]');
        /** @type {HTMLInputElement | null} */
        this.input = this.container.querySelector('[data-quantity-input-collection]');
        /** @type {HTMLElement | null} */
        this.minusBtn = this.container.querySelector('[data-quantity-minus-collection]');
        /** @type {HTMLElement | null} */
        this.plusBtn = this.container.querySelector('[data-quantity-plus-collection]');

        if (this.select) {
            this.select.addEventListener('change', (e) => this.handleSelectChange(e));
        }

        if (this.input) {
            this.input.addEventListener('change', (e) => {
                this.handleInputChange(e);
                this.updateInputWidth();
            });
            this.input.addEventListener('input', (e) => {
                const target = /** @type {HTMLInputElement} */ (e.target);
                if (target.value === '') return;
                const val = parseInt(target.value);
                if (!isNaN(val) && val < 0) target.value = '0';
                this.updateInputWidth();
            });
            this.updateInputWidth();
        }

        if (this.minusBtn) {
            this.minusBtn.addEventListener('click', () => this.updateQuantity(-1));
        }

        if (this.plusBtn) {
            this.plusBtn.addEventListener('click', () => this.updateQuantity(1));
        }
    }

    /**
     * @param {number} delta
     */
    updateQuantity(delta) {
        if (!this.input) return;
        const currentVal = parseInt(this.input.value) || 0;
        const newVal = currentVal + delta;

        const limit = this.settings.dropdownLimit || 9;
        if (newVal <= limit) {
            this.convertToSelect(newVal < this.settings.min ? this.settings.min : newVal);
        } else {
            if (this.settings.max === null || newVal <= this.settings.max) {
                this.input.value = newVal.toString();
                this.updateInputWidth();
                this.triggerChange(newVal);
            }
        }
    }

    /**
     * @param {Event} e
     */
    handleSelectChange(e) {
        const target = /** @type {HTMLSelectElement} */ (e.target);
        const limit = this.settings.dropdownLimit || 9;
        if (target.value === (limit + 1) + '+') {
            this.convertToInput(limit + 1);
        } else {
            this.triggerChange(parseInt(target.value));
        }
    }

    /**
     * @param {Event} e
     */
    handleInputChange(e) {
        const target = /** @type {HTMLInputElement} */ (e.target);
        let val = parseInt(target.value);
        if (isNaN(val)) val = this.settings.min;

        const limit = this.settings.dropdownLimit || 9;
        if (val <= limit) {
            this.convertToSelect(val < this.settings.min ? this.settings.min : val);
        } else {
            if (this.settings.max !== null && val > this.settings.max) {
                val = this.settings.max;
                target.value = val.toString();
            }
            this.triggerChange(val);
        }
    }

    /**
     * @param {number} value
     */
    convertToInput(value) {
        const inputHtml = `
      <div class="flex items-center h-9">
        <div class="relative h-9">
          <input
            type="number"
            name="${this.settings.name}"
            id="${this.settings.id}"
            value="${value}"
            min="${this.settings.lineIndex !== null ? '0' : (this.settings.min || 1)}"
            ${this.settings.max !== null ? `max="${this.settings.max}"` : ''}
            step="1"
            class="h-9 rounded-bl-lg border border-gray-300 bg-white px-2 text-xs font-bold text-gray-900 focus:outline-none focus:ring-1 focus:ring-[#22b573] text-center m-0 relative z-10"
            style="min-width: 36px;"
            ${this.settings.lineIndex !== null ? `data-cart-line="${this.settings.lineIndex + 1}"` : ''}
            data-quantity-input-collection
          >
        </div>

        <div class="flex flex-col h-9 w-8 border border-gray-300 border-l-0 rounded-tr-lg overflow-hidden">
          <button 
            type="button" 
            class="flex-1 flex items-center justify-center bg-white text-gray-600 hover:bg-[#22b573]/10 hover:text-[#22b573] transition-colors focus:outline-none border-b border-gray-200"
            data-quantity-plus-collection
            aria-label="Vermeerder aantal"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M12 4v16m8-8H4"></path>
            </svg>
          </button>
          <button 
            type="button" 
            class="flex-1 flex items-center justify-center bg-white text-gray-600 hover:bg-[#22b573]/10 hover:text-[#22b573] transition-colors focus:outline-none"
            data-quantity-minus-collection
            aria-label="Verminder aantal"
          >
            <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="M20 12H4"></path>
            </svg>
          </button>
        </div>
      </div>
    `;
        this.container.innerHTML = inputHtml + `<script type="application/json" data-settings-collection>${JSON.stringify(this.settings)}</script>`;
        this.init();

        if (this.input) {
            this.updateInputWidth();
            this.input.focus();
            const val = this.input.value;
            this.input.value = '';
            this.input.value = val;
        }
        this.triggerChange(value);
    }

    updateInputWidth() {
        if (!this.input) return;
        const len = this.input.value.length || 1;
        this.input.style.width = (len + 2) + 'ch';
    }

    /**
     * @param {number} value
     */
    convertToSelect(value) {
        let options = '';
        const limit = this.settings.dropdownLimit || 9;
        const startVal = this.settings.min || 1;
        for (let i = startVal; i <= limit; i++) {
            options += `<option value="${i}" ${i === value ? 'selected' : ''}>${i}</option>`;
        }
        const plusVal = limit + 1;
        options += `<option value="${plusVal}+">${plusVal}+</option>`;

        const selectHtml = `
      <div class="relative h-9">
        <select
          name="${this.settings.name}"
          id="${this.settings.id}"
          class="h-9 rounded-bl-lg border border-gray-300 bg-white px-2 text-xs font-bold text-gray-900 focus:outline-none focus:ring-1 focus:ring-[#22b573] text-center m-0 relative z-10"
          ${this.settings.lineIndex !== null ? `data-cart-line="${this.settings.lineIndex + 1}"` : ''}
          data-quantity-select-collection
        >
          ${options}
        </select>
      </div>
    `;
        this.container.innerHTML = selectHtml + `<script type="application/json" data-settings-collection>${JSON.stringify(this.settings)}</script>`;
        this.init();

        this.triggerChange(value);
    }

    /**
     * @param {number} value
     */
    triggerChange(value) {
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            this.container.dispatchEvent(new CustomEvent('quantity:change', {
                bubbles: true,
                detail: {
                    value: value,
                    name: this.settings.name,
                    lineIndex: this.settings.lineIndex
                }
            }));

            if (this.settings.lineIndex !== null) {
                this.container.dispatchEvent(new CustomEvent('quantity-selector:update', {
                    bubbles: true,
                    detail: {
                        quantity: value,
                        cartLine: this.settings.lineIndex + 1
                    }
                }));
            }
        }, 1000);
    }
}

function initQuantitySelectorsCollection() {
    document.querySelectorAll('[data-quantity-selector-collection]').forEach(el => {
        const container = /** @type {HTMLElement} */ (el);
        if (!container.dataset.initialized) {
            new QuantitySelectorCollection(container);
            container.dataset.initialized = 'true';
        }
    });
}

document.addEventListener('DOMContentLoaded', initQuantitySelectorsCollection);
const cartEventsCollection = ['cart:update', 'cart:refresh', 'theme:cart:update', 'cart-update', 'cart:updated'];
cartEventsCollection.forEach(eventName => {
    document.addEventListener(eventName, () => {
        setTimeout(initQuantitySelectorsCollection, 50);
    });
});
document.addEventListener('shopify:section:load', initQuantitySelectorsCollection);
document.addEventListener('page:updated', initQuantitySelectorsCollection);
